import Controller from 'controllers/controller';
import Helpers from 'tools/helpers';
import EventManager from 'tools/event-manager';
import Bus from 'tools/bus';
import Binder from 'tools/binder';
import {TweenLite, Power2} from 'tools/gsap';

/**
 * Singleton class. Use PageManager.getInstance() to fetch the instance.
 */
class PageController extends Controller {
    constructor(app) {
        super(app);
        
        this.events = new EventManager({
            bind: this
        });

        this.bus = new Bus(this);
        this.binder = new Binder(this);

        this.scrollingToPage = false;

        this.elements = { };
        this.currentPage = null;
        this.pages = [ ];
        this.windowWidth = null;
        this.windowHeight = null;
        this.scrollTop = null;

        this.cacheElements();
        this.cachePages();
        this.attachEvents();
    }

    attachEvents() {
        // On IE, there's an annoying issue that'll just take time to fix the scroll position to be incorrect on page
        // load. No time to fix or really work out why it's happening, so we just repeatedly cache dimensions and set
        // the scoll pos for a second. It works at least.
        if (this.app.IEVersion <= 11) {
            // First delayed function will scroll to page
            this.events.on(window, 'load', Helpers.repeatedFunction(4, 200, this.binder.bind('onWindowLoad')));
        } else {
            // First delayed function will scroll to page
            this.events.on(window, 'load', Helpers.repeatedFunction(2, 200, this.binder.bind('onWindowLoad')));
        }
        
        // Subsequent calls merely will cache dimensions
        this.events.on(window, 'load', Helpers.repeatedFunction(3, 200, this.binder.bind('onWindowResize')));
        this.app.on('page-loaded', Helpers.delayedFunction(400, Helpers.repeatedFunction(10, 500, this.onPageLoaded, this), this));
        this.events.on(window, 'resize', Helpers.repeatedFunction(5, 200, this.onWindowResize, this));
        this.events.on(window, 'scroll', Helpers.throttledFunction(200, this.onWindowScroll, this));
    }

    /**
     * Caches dimensions and vertical offsets of pages.
     */
    onWindowLoad() {
        this.cacheDimensions();
        this.app.headerController.update();
        this.updateCurrentPageFromBody();

        // IE and it's usual glitchy shenanigans strikes again, and due to the fact that nobody who uses the browser
        // really cares about the website they're using, let's just hack it rather than waste time on blind eyes.
        if (this.app.IEVersion && ! this.IEFixInterval) {
            this.IEFixInterval = setInterval(() => this.onWindowResize(), 1000);
            setTimeout(() => clearInterval(this.IEFixInterval), 5000);
        }
    }

    onPageLoaded() {
        this.cacheDimensions();
        this.app.headerController.update();
    }

    cacheElements() {
        this.elements.pagesWrapper = document.querySelector('.pages');
        this.elements.pages = Array.from(document.querySelectorAll('.pages .page'));
    }

    cachePages() {
        this.pages = this.elements.pages.map(page => {
            return {
                element: page,
                url: page.getAttribute('data-url'),
                path: page.getAttribute('data-path'),
                template: page.getAttribute('data-template'),
                name: page.getAttribute('data-name'),
                headerColor: page.getAttribute('data-header-color'),
                title: page.getAttribute('data-title'),
                width: null,
                height: null,
                offsetTop: null
            }
        });
    }

    cacheDimensions() {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
        this.scrollTop = Helpers.getScrollTop();

        this.pages.forEach(page => {
            let rect = Helpers.getRect(page.element);

            page.width = rect.width;
            page.height = rect.height;
            page.offsetTop = this.scrollTop + rect.top;
            page.offsetBottom = page.offsetTop + page.height;
        })
    }

    onWindowResize() {
        this.cacheDimensions();
        this.onWindowScroll();
    }

    onWindowScroll() {
        this.scrollTop = Helpers.getScrollTop();

        if (! this.scrollingToPage) {
            this.updateCurrentPage();
        }
    }
    
    getPageByUrl(url) {
        let match = null;
        
        this.pages.forEach(page => {
            if (page.url === url) {
                match = page;
            }
        });
        
        return match;
    }

    updateCurrentPageFromBody(scrollToPage = true) {
        let url = document.body.getAttribute('data-url'),
            page = this.getPageByUrl(url);
        
        if (page) {
            this.setCurrentPage(page);
            
            if (scrollToPage) {
                this.scrollToPage(page, false);
            }
        }
    }
    
    scrollToPage(page, animate = true) {
        this.scrollingToPage = true;

        this.cacheDimensions();
        
        let offset = 0;
        
        // If layers are enabled, offset the scroll so that the top of the page doesn't appear beneath the header.
        if ( ! this.app.headerController.layersEnabled()) {
            offset = -this.app.headerController.height;
        }
        
        TweenLite.to(window, animate ? 1 : 0, {
            scrollTo: {
                y: page.offsetTop + offset,
                autoKill: false
            },
            ease: Power2.easeInOut,
            onComplete: () => this.scrollingToPage = false
        });
    }
    
    updateCurrentPage() {
        const current = this.calculateCurrentPage();

        this.setCurrentPage(current);
    }

    /**
     * Set the current page.
     * 
     * @param {object} page - Page object created in this.cachePages
     * @param {boolean} emit
     */
    setCurrentPage(page, emit = true) {
        // Skip if given page is the same as the current page
        if (this.currentPage && page.path === this.currentPage.path) {
            return;
        }

        this.currentPage = page;

        document.body.setAttribute('data-current-page-template', page.template);
        document.body.setAttribute('data-current-page-path', page.path);
        document.body.setAttribute('data-current-page-name', page.name);
        
        document.title = page.title;
        
        this.emit('change', page);
    }

    /**
     * Calculate which page fills the viewport the most and return it.
     * 
     * @returns {*}
     */
    calculateCurrentPage() {
        let pagesByViewportRatio = { };

        this.pages.forEach(page => {
            let topCutoff = Math.max(0, -(page.offsetTop - this.scrollTop)),
                bottomCutoff = Math.max(0, page.height - this.windowHeight + (page.offsetTop - this.scrollTop)),
                totalCutoff = topCutoff + bottomCutoff,
                viewportSpaceFilled = Math.max(0, page.height - totalCutoff),
                viewportRatio = viewportSpaceFilled / this.windowHeight;

            pagesByViewportRatio[viewportRatio] = page;
        });

        // Return page will fills most of the viewport
        return pagesByViewportRatio[Math.max.apply(Math, Object.keys(pagesByViewportRatio))];
    }
    
    getCurrentPage() {
        return this.currentPage;
    }
}

export default PageController;